import React from 'react';
import { Flex, Text } from '@chakra-ui/react';
import { Modal, Button, OutlineCard } from '@morressier/alembic';
import { useForm } from 'react-hook-form';
import { generateValidationSchema } from 'src/utils/validation';
import { ajvResolver } from '@hookform/resolvers/ajv';
import _ from 'lodash';
import { FormFieldBoolean } from '.';
import { RadioGroup } from 'src/components/RadioGroup';
import MultilineText from 'src/components/MultilineText';
import { FieldLabel } from 'src/components/Field';

interface Props {
  isOpen: boolean;
  onSubmit: (value: boolean) => void;
  onCancel: () => void;
  formField: FormFieldBoolean;
  value: boolean;
}

type IBooleanFormState = {
  [key: string]: boolean;
};

const BooleanUpdateModal: React.FC<Props> = props => {
  const { isOpen, onSubmit, onCancel, formField, value } = props;

  const schema = generateValidationSchema({
    [formField.name]: formField
  });

  const fieldName = formField.caseDataFields.main;

  const methods = useForm({
    defaultValues: _.set({} as IBooleanFormState, fieldName, value),
    resolver: ajvResolver(schema)
  });

  const labels = formField.params.labels;

  const options = [
    {
      value: true,
      children: (
        <>
          <Text>{labels.options['true']?.main}</Text>
          <MultilineText text={labels.options['true']?.secondary || ''} />
        </>
      )
    },
    {
      value: false,
      children: (
        <>
          <Text>{labels.options['false']?.main}</Text>
          <MultilineText text={labels.options['false']?.secondary || ''} />
        </>
      )
    }
  ];

  const { watch, handleSubmit, control } = methods;

  const fieldValue = watch(fieldName);

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {}}
      width="755px"
      closeButtonProps={{
        hidden: true
      }}
    >
      <Flex flexDirection="column" gap={8}>
        <form
          onSubmit={handleSubmit(() => {
            onSubmit(fieldValue);
          })}
        >
          <OutlineCard borderless padding={8} width="100%">
            <Flex flexDirection="column" gap={4}>
              <Flex flexDirection="column" gap={2}>
                <Text textStyle="headingSection">{`Update ${formField.params.labels.main}`}</Text>
              </Flex>
              <Flex flexDirection="column" gap={2}>
                <Flex flexDirection="column" gap={6} alignItems="flex-start">
                  <Flex flex={1} width={'100%'} gap={4} flexDirection="column">
                    {['GeneratedDataset', 'UtilizedDataset'].includes(
                      formField.name
                    ) && (
                      <FieldLabel
                        isRequired={false}
                        label={`${
                          formField.name === 'GeneratedDataset'
                            ? 'Did your work generate any major datasets (e.g., DNA sequence data, clinical trial data, field data)?'
                            : 'Did your work use any previously published major datasets (e.g., DNA sequence data, clinical trial data, field data)?'
                        }`}
                        subText=""
                        helpText={labels.helpText}
                      />
                    )}

                    <RadioGroup
                      control={control}
                      name={fieldName}
                      options={options}
                      gap={2}
                    />
                  </Flex>
                </Flex>
                <Flex gap={2} justifyContent="flex-end">
                  <Button
                    variant="secondary"
                    onClick={onCancel}
                    borderRadius="small"
                  >
                    Exit Without Saving
                  </Button>
                  <Button variant="primary" type="submit" borderRadius="small">
                    Save Changes
                  </Button>
                </Flex>
              </Flex>
            </Flex>
          </OutlineCard>
        </form>
      </Flex>
    </Modal>
  );
};

export default BooleanUpdateModal;
